import React from "react";
import { Helmet } from "react-helmet-async";
import { motion, useReducedMotion } from "framer-motion";
import {
  FaEnvelope,
  FaHandshake,
  FaLanguage,
  FaHeadset,
  FaYoutube,
  FaFacebook,
} from "react-icons/fa";
import { SiX } from "react-icons/si";

import "./Contact.css";

const SOCIAL_LINKS = [
  {
    name: "YouTube",
    url: "https://youtube.com/@LingoLabAI",
    icon: <FaYoutube />,
  },
  {
    name: "X (Twitter)",
    url: "https://twitter.com/LingoLabAI",
    icon: <SiX />,
  },
  {
    name: "Facebook",
    url: "https://facebook.com/LingoLabAI",
    icon: <FaFacebook />,
  },
];

const Contact = () => {
  const shouldReduceMotion = useReducedMotion();

  const contactItems = [
    {
      title: "General Information Inquiries",
      description: "For general questions about LingoLab.",
      email: "info@lingolab.ca",
      icon: <FaEnvelope />,
    },
    {
      title: "Cooperation and Sales Inquiries",
      description: "For partnerships, sales, or business opportunities.",
      email: "sales@lingolab.ca",
      icon: <FaHandshake />,
    },
    {
      title: "Translation Related Inquiries",
      description: "For questions about translation services.",
      email: "translations@lingolab.ca",
      icon: <FaLanguage />,
    },
    {
      title: "Technical Support",
      description: "For help with technical issues or support.",
      email: "support@lingolab.ca",
      icon: <FaHeadset />,
    },
  ];

  return (
    <div className="contact-container">
      <Helmet>
        <title>Contact LingoLab – AI Video Translation Support</title>
        <meta
          name="description"
          content="Contact LingoLab for support, sales, translation inquiries, or general information. Our team responds within 24 hours."
        />
        <meta
          name="keywords"
          content="Contact LingoLab, support, sales, translation, AI video translation, customer service"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://lingolab.ca/contact" />
        <meta
          property="og:title"
          content="Contact LingoLab – AI Video Translation Support"
        />
        <meta
          property="og:description"
          content="Get in touch with LingoLab for AI video translation support, sales, and service inquiries."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lingolab.ca/contact" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@LingLabAI" />
        <meta
          name="twitter:title"
          content="Contact LingoLab – AI Video Translation Support"
        />
        <meta
          name="twitter:description"
          content="Get in touch with LingoLab for AI video translation support, sales, and service inquiries."
        />
        <meta name="twitter:image" content="https://lingolab.ca/ll-X.png" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "LingoLab",
              "url": "https://lingolab.ca/",
              "logo": "https://lingolab.ca/ll-X.png",
              "sameAs": [
                "https://twitter.com/LingLabAI"
              ],
              "description": "LingoLab uses advanced AI to translate and localize your videos into 80+ languages, preserving the original voice and syncing lips for a native look.",
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "support@lingolab.ca",
                "contactType": "customer support",
                "url": "https://lingolab.ca/contact"
              }
            }
          `}
        </script>
      </Helmet>
      <motion.header
        className="contact-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: shouldReduceMotion ? 0 : 0.4,
          ease: "easeOut",
        }}
      >
        <h1 className="contact-title">Get in Touch</h1>
        <p className="contact-subtitle">
          Reach out to our team for assistance—we respond within 24 hours.
        </p>
        <div className="header-divider"></div>
      </motion.header>

      <div className="contact-list">
        {contactItems.map((item, index) => (
          <motion.div
            key={index}
            className="contact-card"
            role="article"
            aria-labelledby={`contact-title-${index}`}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{
              duration: shouldReduceMotion ? 0 : 0.3,
              delay: index * 0.1,
            }}
            whileHover={
              shouldReduceMotion
                ? {}
                : { y: -5, boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)" }
            }
          >
            <div className="contact-icon">{item.icon}</div>
            <div className="contact-content">
              <h2 className="contact-item-title" id={`contact-title-${index}`}>
                {item.title}
              </h2>
              <p className="contact-description">{item.description}</p>
              <a
                href={`mailto:${item.email}`}
                className="contact-email"
                aria-label={`Email ${item.title} at ${item.email}`}
                onTouchStart={() => {}}
              >
                {item.email}
              </a>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Social Media Section */}
      <div className="contact-social">
        <h2 className="contact-social-title">Connect with us</h2>
        <div className="contact-social-icons">
          {SOCIAL_LINKS.map((link) => (
            <a
              key={link.name}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={link.name}
              className="contact-social-link"
            >
              {link.icon}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Contact;
